import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import ModeSwitcher from './ModeSwitcher';
import TakeoffSelector from './TakeoffSelector';
import AreasMode from './modes/AreasMode';
import TakeoffMode from './modes/TakeoffMode';
import ProjectsMode from './modes/ProjectsMode';
import AreaEditor from '../editor/AreaEditor';

const useStyles = makeStyles(() => ({
  sidebar: {
    width: 360,
    minWidth: 360,
    background: '#ffffff',
    borderLeft: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  switcherWrap: {
    padding: '16px 16px 0 16px',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: 16,
  },
  saveBar: {
    padding: '12px 16px',
    borderTop: '1px solid #e0e0e0',
    background: '#fafafa',
  },
  saveBtn: {
    textTransform: 'none',
    fontWeight: 500,
  },
}));

const Sidebar = ({
  activeMode,
  onModeChange,
  editingAreaId,
  onEditArea,
  onEditAreaClose,
  jobsite,
  areas = [],
  onDrawArea,
  onUpdateArea,
  onDeleteArea,
  onSaveTakeoff,
  takeoffs = [],
  takeoff,
  saving,
  dirty,
  onNewTakeoff,
  onSwitchTakeoff,
  onDeleteTakeoff,
  onRenameTakeoff,
  // Shape editing
  editingShapeAreaId,
  onStartEditShape,
  onStopEditShape,
  // Legacy import
  hasLegacyAreas,
  legacyAreas = [],
  onImportLegacyAreas,
  url,
  // Overlay manager
  overlayManager,
}) => {
  const classes = useStyles();
  const editingArea = editingAreaId ? areas.find(a => (a._id || a.id) === editingAreaId) : null;

  const renderSaveBar = () => {
    if (!jobsite?._id) return null;
    const label = saving ? 'Saving…' : dirty ? 'Save Takeoff' : 'All saved';
    return (
      <div className={classes.saveBar}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="small"
          className={classes.saveBtn}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={() => onSaveTakeoff(takeoff?.name)}
          disabled={saving || !dirty || areas.length === 0}
        >
          {label}
        </Button>
      </div>
    );
  };

  if (editingArea) {
    const isEditingShape = editingShapeAreaId === editingAreaId;
    return (
      <div className={classes.sidebar}>
        <AreaEditor
          area={editingArea}
          onClose={onEditAreaClose}
          onUpdate={(updates) => onUpdateArea(editingAreaId, updates)}
          onDelete={() => { onDeleteArea(editingAreaId); onEditAreaClose(); }}
          isEditingShape={isEditingShape}
          onStartEditShape={() => onStartEditShape(editingAreaId)}
          onStopEditShape={onStopEditShape}
        />
        {renderSaveBar()}
      </div>
    );
  }

  return (
    <div className={classes.sidebar}>
      {jobsite?._id && (
        <TakeoffSelector
          takeoffs={takeoffs}
          activeTakeoffId={takeoff?._id}
          activeTakeoffName={takeoff?.name || ''}
          onSwitchTakeoff={onSwitchTakeoff}
          onNewTakeoff={onNewTakeoff}
          onDeleteTakeoff={onDeleteTakeoff}
          onRenameTakeoff={onRenameTakeoff}
          disabled={saving}
        />
      )}

      <div className={classes.switcherWrap}>
        <ModeSwitcher activeMode={activeMode} onModeChange={onModeChange} />
      </div>

      <div className={classes.content}>
        {activeMode === 'areas' && (
          <AreasMode
            jobsite={jobsite}
            areas={areas}
            onDrawArea={onDrawArea}
            onEditArea={onEditArea}
            hasLegacyAreas={hasLegacyAreas}
            legacyAreaCount={legacyAreas.length}
            onImportLegacyAreas={onImportLegacyAreas}
            saving={saving}
          />
        )}
        {activeMode === 'takeoff' && (
          <TakeoffMode
            areas={areas}
            rateOverrides={takeoff?.productionRates || {}}
          />
        )}
        {activeMode === 'projects' && (
          <ProjectsMode
            url={url}
            jobsite={jobsite}
            overlays={overlayManager?.overlays || []}
            activeOverlayId={overlayManager?.activeOverlayId}
            uploading={overlayManager?.uploading}
            savedOverlayUrls={overlayManager?.savedOverlayUrls || []}
            onUploadFile={overlayManager?.uploadOverlay}
            onAddExistingOverlay={overlayManager?.addExistingOverlay}
            onSetVisible={overlayManager?.setOverlayVisible}
            onSetOpacity={overlayManager?.setOverlayOpacity}
            onSetMasterOpacity={overlayManager?.setMasterOpacity}
            onSetLocked={overlayManager?.setOverlayLocked}
            onSetCategory={overlayManager?.setOverlayCategory}
            onActivate={overlayManager?.activateOverlay}
            onRemove={overlayManager?.removeOverlay}
          />
        )}
      </div>

      {renderSaveBar()}
    </div>
  );
};

export default Sidebar;
